import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Eye, FilterX, Inbox, RefreshCw, Search, X } from "lucide-react";
import { WARRANTY_JOURNEY_URL } from "../../Shared/constant";
import { Badge } from "../../Franchise/D2CDashboard/ui/badge";
import { Button } from "../../Franchise/D2CDashboard/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "../../Franchise/D2CDashboard/ui/card";
import { Input, NativeSelect } from "../../Franchise/D2CDashboard/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  clickableRow,
} from "../../Franchise/D2CDashboard/ui/table";
import { EmptyState, ErrorState, SkeletonRows } from "../../Franchise/D2CDashboard/StateBlock";
import { WARRANTY_PERMISSION, hasOneOfWarrantyPermissions } from "../common/warrantyAccess";
import { searchAllWarranties } from "../common/warrantyApi";
import { usePagedList } from "../common/usePagedList";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { formatDate } from "../common/format";
import {
  PAGE_SIZE,
  PLAN_FILTERS,
  PLAN_LABEL,
  SOURCE_FILTERS,
  SOURCE_LABEL,
  STATUS_FILTERS,
  STATUS_LABEL,
  filtersFromParams,
  filtersToParams,
  hasFilters,
  planPill,
  referralSummary,
  saleChannelLabel,
  searchBody,
  searchMessage,
  sourceLabel,
  statusLabel,
  statusTone,
} from "./allWarrantiesList";
import { NoWarrantyAccess, PageHeader, Shell } from "./Shell";

const ReferralCell = ({ referral }) => {
  const summary = referralSummary(referral);
  if (!summary) return <span className="tw-text-muted-foreground">-</span>;
  return (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
      <span className="tw-font-mono tw-text-xs tw-font-medium">{summary.code}</span>
      <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-muted-foreground">
        {summary.owner} · {summary.status}
        {summary.incentive === true && (
          <span title="Earns an incentive" className="tw-inline-flex tw-items-center tw-text-emerald-700">
            <Check className="tw-size-3.5" aria-hidden="true" />
            <span className="tw-sr-only">Earns an incentive</span>
          </span>
        )}
        {summary.incentive === false && (
          <span title="No incentive" className="tw-inline-flex tw-items-center">
            <X className="tw-size-3.5" aria-hidden="true" />
            <span className="tw-sr-only">No incentive</span>
          </span>
        )}
      </span>
    </div>
  );
};

export const WarrantyRow = ({ item, onOpen }) => {
  const plan = planPill(item.plan);
  const open = () => onOpen(item);
  return (
    <TableRow
      role="button"
      tabIndex={0}
      aria-label={`Open the journey of ${item.frameNumber || item.registrationId}`}
      className={clickableRow}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          open();
        }
      }}
    >
      <TableCell className="tw-font-mono tw-text-xs tw-font-medium tw-text-primary">{item.frameNumber || "-"}</TableCell>
      <TableCell className="tw-max-w-[220px] tw-truncate" title={item.productName || undefined}>
        {item.productName || "-"}
      </TableCell>
      <TableCell>
        <Badge tone={statusTone(item.status)}>{statusLabel(item.status)}</Badge>
      </TableCell>
      <TableCell>
        <Badge tone={plan.tone}>{plan.text}</Badge>
      </TableCell>
      <TableCell className="tw-text-muted-foreground">{formatDate(item.registeredAt)}</TableCell>
      <TableCell>
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span>{sourceLabel(item.registeredVia, item.legacySource)}</span>
          {item.saleChannel && (
            <span className="tw-text-xs tw-text-muted-foreground">Sold via {saleChannelLabel(item.saleChannel)}</span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <ReferralCell referral={item.referral} />
      </TableCell>
      <TableCell>
        {item.underReview ? (
          <Badge tone="warning">
            <Eye className="tw-size-3" aria-hidden="true" />
            Under review
          </Badge>
        ) : (
          <span className="tw-text-muted-foreground">-</span>
        )}
      </TableCell>
    </TableRow>
  );
};

// Every registration, latest first. The filters live in the URL so a reload keeps them; the typed
// search (a phone, a frame number or a registration id) is held in state only and posted in the
// body with the filters, never put in the URL or the browser history.
const AllWarranties = () => {
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasOneOfWarrantyPermissions(selfInfo, [WARRANTY_PERMISSION.REVIEW, WARRANTY_PERMISSION.VIEW]);
  useWarrantyNav(allowed);
  const navigate = useNavigate();

  const [params, setParams] = useSearchParams();
  const paramText = params.toString();
  const filters = useMemo(() => filtersFromParams(new URLSearchParams(paramText)), [paramText]);
  const setFilter = (patch) => setParams(filtersToParams({ ...filters, ...patch }), { replace: true });

  // `text` is what is typed; `query` is the search applied to the list.
  const [text, setText] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [query, setQuery] = useState("");

  const { status, source, plan, from, to } = filters;
  const fetchPage = useCallback(
    (cursor) =>
      searchAllWarranties(accessToken, searchBody({ status, source, plan, from, to }, query, cursor, PAGE_SIZE)),
    [accessToken, query, status, source, plan, from, to]
  );
  const list = usePagedList(fetchPage, allowed);

  const runSearch = (event) => {
    event.preventDefault();
    const message = searchMessage(text);
    setFieldError(message);
    if (message) return;
    setQuery(text.trim());
  };

  const clearSearch = () => {
    setText("");
    setFieldError("");
    setQuery("");
  };

  const open = (item) => navigate(`${WARRANTY_JOURNEY_URL}/${encodeURIComponent(item.registrationId)}`);

  if (!allowed) {
    return (
      <Shell>
        <NoWarrantyAccess />
      </Shell>
    );
  }

  const searching = query !== "";

  return (
    <Shell>
      <PageHeader
        title="All warranties"
        sub="Every warranty registration, latest first. Market IN. Open a row to see its journey."
        actions={
          <Button
            variant="outline"
            size="icon"
            onClick={() => list.reload(false)}
            disabled={list.status === "loading"}
            aria-label="Reload the list"
            title="Reload the list"
          >
            <RefreshCw aria-hidden="true" />
          </Button>
        }
      />

      <Card className="tw-mb-4">
        <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
          <form className="tw-flex tw-flex-wrap tw-items-start tw-gap-2" onSubmit={runSearch} role="search">
            <div className="tw-flex tw-min-w-[260px] tw-flex-1 tw-flex-col tw-gap-1">
              <div className="tw-relative">
                <Search
                  className="tw-pointer-events-none tw-absolute tw-left-2.5 tw-top-1/2 tw-size-4 -tw-translate-y-1/2 tw-text-muted-foreground"
                  aria-hidden="true"
                />
                <Input
                  value={text}
                  maxLength={40}
                  autoComplete="off"
                  aria-label="Search by frame number, phone or registration id"
                  placeholder="Frame number, phone or registration id"
                  invalid={Boolean(fieldError)}
                  className="tw-pl-8"
                  onChange={(event) => {
                    setText(event.target.value);
                    if (fieldError) setFieldError("");
                  }}
                />
              </div>
              {fieldError && (
                <span role="alert" className="tw-text-xs tw-text-red-700">
                  {fieldError}
                </span>
              )}
            </div>
            <Button type="submit">Search</Button>
            {searching && (
              <Button variant="outline" onClick={clearSearch}>
                <X aria-hidden="true" /> Clear search
              </Button>
            )}
          </form>

          <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-2">
            <NativeSelect
              aria-label="Status"
              value={status}
              className="tw-w-48"
              onChange={(event) => setFilter({ status: event.target.value })}
            >
              {STATUS_FILTERS.map((key) => (
                <option key={key || "any"} value={key}>
                  {STATUS_LABEL[key]}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label="Source"
              value={source}
              className="tw-w-40"
              onChange={(event) => setFilter({ source: event.target.value })}
            >
              {SOURCE_FILTERS.map((key) => (
                <option key={key || "any"} value={key}>
                  {SOURCE_LABEL[key]}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label="Plan"
              value={plan}
              className="tw-w-36"
              onChange={(event) => setFilter({ plan: event.target.value })}
            >
              {PLAN_FILTERS.map((key) => (
                <option key={key || "any"} value={key}>
                  {PLAN_LABEL[key]}
                </option>
              ))}
            </NativeSelect>
            <label className="tw-flex tw-flex-col tw-gap-0.5 tw-text-xs tw-text-muted-foreground">
              Registered from
              <Input
                type="date"
                value={from}
                max={to || undefined}
                  className="tw-w-40"
                onChange={(event) => setFilter({ from: event.target.value })}
              />
            </label>
            <label className="tw-flex tw-flex-col tw-gap-0.5 tw-text-xs tw-text-muted-foreground">
              Registered to
              <Input
                type="date"
                value={to}
                min={from || undefined}
                  className="tw-w-40"
                onChange={(event) => setFilter({ to: event.target.value })}
              />
            </label>
            {hasFilters(filters) && (
              <Button variant="ghost" onClick={() => setParams({}, { replace: true })}>
                <FilterX aria-hidden="true" /> Clear filters
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader className="tw-items-center">
          <div>
            <CardTitle>{searching ? "Search results" : "Registrations"}</CardTitle>
            <CardDescription>Latest registration first.</CardDescription>
          </div>
        </CardHeader>
        {list.status === "loading" && <SkeletonRows />}
        {list.status === "error" && (
          <div className="tw-p-4 tw-pt-0">
            <ErrorState message={list.message} onRetry={() => list.reload(false)} />
          </div>
        )}
        {list.status === "ready" && list.items.length === 0 && (
          <EmptyState icon={Inbox} title={searching ? "No registration matches" : "No registrations match these filters"}>
            {searching ? "Check what you typed, or clear a filter." : "Try another status or clear a filter."}
          </EmptyState>
        )}
        {list.status === "ready" && list.items.length > 0 && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Frame</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Registered on</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Referral</TableHead>
                  <TableHead>Under review</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.items.map((item) => (
                  <WarrantyRow key={item.registrationId} item={item} onOpen={open} />
                ))}
              </TableBody>
            </Table>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-3 tw-p-4">
              {list.moreError && (
                <span role="alert" className="tw-text-xs tw-text-red-700">
                  {list.moreError}
                </span>
              )}
              {list.nextCursor ? (
                <Button variant="outline" onClick={list.loadMore} disabled={list.loadingMore}>
                  {list.loadingMore ? "Loading" : "Load more"}
                </Button>
              ) : (
                <span className="tw-text-xs tw-text-muted-foreground">
                  {list.items.length.toLocaleString("en-IN")} shown. That is all.
                </span>
              )}
            </div>
          </>
        )}
      </Card>
    </Shell>
  );
};

export default AllWarranties;
